import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectScreensaverMode } from '../../state/screensaver/screensaverSelectors';
import { CLICK } from '../../util/supportedEvents';
import constants from '../../settings/constants';
import SeriesGallery from '../SeriesGallery';
import { selectDialog } from '../../state/dialog/dialogSelectors';
import { actionSetScreensaverMode } from '../../state/screensaver/screensaverActions';
import { ScreensaverModes } from './ScreenSaver';
import { useKioskIntegration } from '../../hooks/useKioskIntegration';

const MODES_WITH_LISTENER = [
  constants.SCREENSAVER_MODE.idle,
  constants.SCREENSAVER_MODE.slideshow,
];

interface Props {
  screensaverModes: ScreensaverModes;
  countdown: number;
  timeLeft: number;
  resetTimer: () => void;
}

const useModeHandler = ({
  screensaverModes,
  timeLeft,
  resetTimer,
  countdown,
}: Props) => {
  const functionRef = React.useRef(() => {});

  const screensaverMode = useSelector(selectScreensaverMode);
  const dialog = useSelector(selectDialog);

  const { isUpptacka } = useKioskIntegration();

  const dispatch = useDispatch();

  const dispatchMode = (mode: string) =>
    dispatch(actionSetScreensaverMode(mode));
  const setMode = (mode: string) => {
    removeListener();
    dispatchMode(mode);
  };

  /**
   * On user interaction
   */
  const handleClick = () => {
    if (screensaverMode !== constants.SCREENSAVER_MODE.idle) {
      const { onEnd = () => {} } = screensaverModes[screensaverMode].handlers;
      onEnd();
      setMode(constants.SCREENSAVER_MODE.idle);
    }

    resetTimer();
  };

  /**
   * should set countdown mode
   */
  const shouldSetCountdownMode = () =>
    !isUpptacka && timeLeft === countdown && !(dialog.type === SeriesGallery);

  /**
   * Add reference to function to be able to remove later and add it as a listener
   */
  const addListener = () => {
    functionRef.current = handleClick;
    document.body.addEventListener(CLICK, functionRef.current, true);
  };

  /**
   * Removes a listener previously added by addListener.
   */
  const removeListener = () => {
    document.body.removeEventListener(CLICK, functionRef.current, true);
  };

  /**
   * Renders screen saver by mode
   */
  const renderScreenSaver = () =>
    !isUpptacka && screensaverModes.hasOwnProperty(screensaverMode)
      ? screensaverModes[screensaverMode].component()
      : null;

  /**
   * On timeLeft change
   */
  React.useEffect(() => {
    if (shouldSetCountdownMode()) setMode(constants.SCREENSAVER_MODE.countdown);
    if (timeLeft === 0) setMode(constants.SCREENSAVER_MODE.slideshow);
  }, [timeLeft]);

  /**
   * On screensaverMode change
   */
  React.useEffect(() => {
    if (MODES_WITH_LISTENER.indexOf(screensaverMode) > -1) addListener();
    const { onStart = () => {} } = screensaverModes[screensaverMode].handlers;
    onStart();
  }, [screensaverMode]);

  /**
   * On componentDidMount
   */
  React.useEffect(() => {
    return () => removeListener();
  }, []);

  return {
    component: renderScreenSaver(),
    mode: screensaverMode,
  };
};

export default useModeHandler;
